import { useEffect, useState } from 'react';
import { Box, Card, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from '../../store';
import { setTravelDetail } from '../../store/slices/userInputSlice';

import { eventsTracker } from '../../utils/ctEventsTracking';
import { usePostHog } from 'posthog-js/react';
import { OAImage } from '../../components';

interface FlightSearchItemProps {
  item: {
    from?: { iata: string; city: string };
    to?: { iata: string; city: string };
    tripStart?: { date: Date };
    tripEnd?: { date: Date };
    travellerCount?: {
      adult?: number;
      child?: number;
      infant?: number;
    };
    cabinType?: string;
    isReturn?: boolean;
    tripType?: string;
    fareGroup?: string;
  };
}

const RecentFlightSearchItem = ({ item }: FlightSearchItemProps) => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [startTime, setStartTime] = useState(Date.now());

  useEffect(() => {
    setStartTime(Date.now());
  }, []);

  const handleRecentSearch = () => {
    const totalScreenDuration = Math.floor((Date.now() - startTime) / 1000);

    const totalTravelers =
      (item?.travellerCount?.adult || 0) +
      (item?.travellerCount?.child || 0) +
      (item?.travellerCount?.infant || 0);

    eventsTracker(
      {
        flowName: 'Flight',
        screenName: 'Flight',
        ctaAction: 'RecSearch',
        screenDuration: totalScreenDuration?.toString(),
        otherData: {
          from: item?.from?.iata,
          to: item?.to?.iata,
          departureDate: item?.tripStart?.date,
          class: item?.cabinType,
          numberOfTravellers: totalTravelers,
        },
      },
      posthog
    );

    // here in item, we ain't getting tripType so we are setting it here
    dispatch(
      setTravelDetail({
        ...item,
        tripType: item?.tripEnd?.date ? 'R' : 'O',
      })
    );

    const { travellerCount = {}, tripStart, tripEnd, cabinType, fareGroup } = item;
    const { adult = 0, child = 0, infant = 0 } = travellerCount;

    const tripType = tripEnd?.date ? 'R' : 'O';
    const traveller = `adults=${adult}&childs=${child}&infants=${infant}`;

    const fromTo =
      `${item?.from?.iata}` +
      `&fromCity=${item?.from?.city}` +
      `&to=${item?.to?.iata}` +
      `&toCity=${item?.to?.city}` +
      `&fromDate=${item?.tripStart?.date}` +
      `${item?.tripEnd?.date ? `&toDate=${item?.tripEnd?.date}` : ''}` +
      `${item?.fareGroup ? `&fareGroup=${item?.fareGroup}` : ''}`;

    navigate(`results?tripType=${tripType}&cabin=${cabinType}&${traveller}&from=${fromTo}`);
  };

  const totalPassengers = Object?.values(item?.travellerCount || {})?.reduce(
    (total, count) => total + (count || 0),
    0
  );
  return (
    <Card
      sx={{
        p: '12px 15px',
        mr: '15px',
        cursor: 'pointer',
      }}
      onClick={handleRecentSearch}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="body1">{item?.from?.iata}</Typography>

        <OAImage
          src={item?.tripEnd?.date ? 'two-way-arrow.svg' : 'one-way-arrow.svg'}
          folder="icons"
          alt="arrow"
          sx={{ mx: '4px' }}
        />

        <Typography variant="body1">{item?.to?.iata}</Typography>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mr="15px">
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {item?.tripStart?.date ? format(item?.tripStart?.date, 'd MMM yyyy') : ''}
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary', mx: '4px' }}>
          &bull;
        </Typography>
        <Box display="flex" alignItems="center">
          <OAImage src="person1.svg" folder="icons" alt="person" />
          <Typography variant="caption" sx={{ color: 'text.secondary', ml: '4px' }}>
            {`${totalPassengers}`}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

export default RecentFlightSearchItem;
